import { create } from 'zustand';
import { useCallback, useContext } from 'react';
import { useWorkspaceId } from '../useWorkspaceHooks';
import { CallaboApiClient } from 'callabo-api/src/CallaboApiClient';
import {
  BaseWorkspaceRole,
  WorkspaceInviteStatus,
  WorkspaceJoinRequestStatus,
} from 'callabo-api/src';
import { useFeatureAccess } from 'app/[locale]/hooks/useFeatureAccess';
import { Feature } from 'app/[locale]/hooks/features';
import { AuthContext } from '@/contexts/AuthContext';

export type WorkspaceInvitee = {
  id: number;
  email: string;
  baseRole: BaseWorkspaceRole;
  workspaceRoles: number[];
  invitedAt: Date;
  status: WorkspaceInviteStatus;
};

export type WorkspaceJoinRequest = {
  id: number;
  name: string;
  email: string;
  createdAt: Date;
  status: WorkspaceJoinRequestStatus;
};

type WorkspaceMemberStore = {
  invitees: WorkspaceInvitee[];
  joinRequests: WorkspaceJoinRequest[];
};

const useWorkspaceMemberStore = create<WorkspaceMemberStore>(() => ({
  invitees: [],
  joinRequests: [],
}));

export const useWorkspaceInvitees = () => {
  return useWorkspaceMemberStore((state) => state.invitees);
};

export const getWorkspaceInvitees = () => {
  return useWorkspaceMemberStore.getState().invitees;
};

export const setWorkspaceInvitees = (invitees: WorkspaceInvitee[]) => {
  useWorkspaceMemberStore.setState({
    invitees,
  });
};

export const useWorkspaceJoinRequests = () => {
  return useWorkspaceMemberStore((state) => state.joinRequests);
};

export const getWorkspaceJoinRequests = () => {
  return useWorkspaceMemberStore.getState().joinRequests;
};

export const setWorkspaceJoinRequests = (joinRequests: WorkspaceJoinRequest[]) => {
  useWorkspaceMemberStore.setState({
    joinRequests,
  });
};

export const useFetchInvitedMembers = () => {
  const { authState } = useContext(AuthContext);
  const workspaceId = useWorkspaceId();
  const featureAccess = useFeatureAccess();

  return useCallback(async () => {
    if (authState.type !== 'signedIn') {
      return;
    }

    if (!featureAccess(Feature.WORKSPACE_INVITEE_VIEW)) {
      return;
    }

    const { callaboApiClient } = authState;

    const response =
      await callaboApiClient.workspaceInvite.getWorkspaceInvitesV1WorkspacesSlugInvitesGet(
        workspaceId
      );
    useWorkspaceMemberStore.setState({
      invitees: response.map((invite) => ({
        email: invite.email,
        id: invite.id,
        baseRole: invite.base_role,
        workspaceRoles: invite.workspace_invite_roles.map((role) => role.workspace_role.id),
        invitedAt: new Date(invite.invited_at),
        status: invite.status,
      })),
    });
  }, [authState.type, workspaceId]);
};

export const useFetchJoinRequests = () => {
  const workspaceId = useWorkspaceId();
  const featureAccess = useFeatureAccess();

  return useCallback(
    async (callaboApiClient: CallaboApiClient) => {
      if (!featureAccess(Feature.WORKSPACE_JOIN_REQUEST_VIEW)) {
        return;
      }

      const response =
        await callaboApiClient.workspace.listJoinRequestsV1WorkspaceSlugJoinRequestsGet(
          workspaceId
        );

      useWorkspaceMemberStore.setState({
        joinRequests: response.map((request) => ({
          id: request.user.id,
          name: request.user.name,
          email: request.user.email,
          createdAt: new Date(request.created_at),
          status: request.status,
        })),
      });
    },
    [workspaceId]
  );
};

export function useJoinRequestedCount() {
  return useWorkspaceMemberStore(
    (state) =>
      state.joinRequests.filter(
        (request) => request.status === WorkspaceJoinRequestStatus.REQUESTED
      ).length
  );
}
